import { Controller } from '@hotwired/stimulus'

// Issue #13062 (S-4) / #13060 (S-2⑥)
// 調整依頼一覧・①架電ラウンドビュー内のステータス・差し戻し解除の編集。
//
// 既存 facility_tasks の一括保存基盤（FacilityTaskBulkUpdatesAdmin / FacilityTask::UpdateForm）を再利用する。
// hidden の id / last_updated_at は初期 disabled（未変更行は送信しない）。値を変更した行だけ
// enable して送信させることで、変更行のみ一括保存される。
//
// Stimulus 登録名はパス由来（admin--ca--request--adjustment-requests--index--component）。
// 既存 facility_tasks の component_controller.js と同じ自動登録パターンに従う。
export default class extends Controller {
  static values = {
    facilityTaskId: Number,
  }

  // Issue #13282: 一括保存後（POST→リダイレクト→全リロード）もスクロール位置を維持する。
  // 保存直前にスクロール位置を sessionStorage に退避し、読み込み後にここで復元する。
  // コントローラは各行＋フォームに付くため複数回 connect されるが、復元は removeItem で1回だけ行う。
  // 重要: Trestle 管理画面は window ではなく `.app-container`(overflow-y:auto) がスクロールするため、
  // window.scrollY ではなくスクロールコンテナの scrollTop を保存・復元する。
  static SCROLL_KEY = 'adjustmentRequestsScrollY'

  // 実際にスクロールする要素（Trestle は .app-container）。無ければ document/window にフォールバック。
  scrollContainer () {
    return document.querySelector('.app-container') || document.scrollingElement || document.documentElement
  }

  connect () {
    const saved = sessionStorage.getItem(this.constructor.SCROLL_KEY)
    if (saved === null) return
    sessionStorage.removeItem(this.constructor.SCROLL_KEY)
    const y = parseInt(saved, 10)
    if (Number.isNaN(y) || y <= 0) return

    // Trestle(bundle.js) が読込時に .app-container の scrollTop を 0 に戻すため、rAF を数フレーム＋
    // 遅延 setTimeout で復元を再適用し「後勝ち」させる（best-effort のスクロール位置復元）。
    const container = this.scrollContainer()
    let frames = 0
    const reapply = () => {
      container.scrollTop = y
      frames += 1
      if (frames < 12) requestAnimationFrame(reapply)
    }
    requestAnimationFrame(reapply)
    ;[80, 200, 400, 700].forEach((ms) => setTimeout(() => { container.scrollTop = y }, ms))
  }

  // ステータス / 差し戻し解除 が変更されたら、その行の id / last_updated_at を送信対象にする。
  enable () {
    const id = this.facilityTaskIdValue
    const idField = document.getElementById(
      `ca_request_facility_task_update_form_facility_task_attributes_${id}_id`,
    )
    const lastUpdatedAtField = document.getElementById(
      `ca_request_facility_task_update_form_facility_task_attributes_${id}_last_updated_at`,
    )
    if (idField) idField.disabled = false
    if (lastUpdatedAtField) lastUpdatedAtField.disabled = false
  }

  // Issue #13405: 対応ステータスが再架電系のときだけ「再架電予定日時」欄を表示する。
  // 行(tr)に付いたコントローラの this.element からその行の欄を取得し、status変更で即時トグルする。
  toggleNextCallAt (event) {
    const field = this.element.querySelector('.next-call-at-field')
    if (!field) return

    const redialStatuses = ['redial', 'redial_waiting_for_schedule']
    field.hidden = !redialStatuses.includes(event.target.value)
  }

  // Issue #13268 (PdM確認: ハイブリッド): 保存前チェック。
  // 「対応ステータス=見学設定済(completed) かつ 見学予定日(scheduled_visit_at) 未入力」の行を検知したら、
  // 該当行をハイライト＋エラー表示し、POST 自体をブロックする（サーバー側ガードは安全網として別途維持）。
  // 違反が無ければ従来どおり送信され一括保存される。
  validateBeforeSubmit (event) {
    const rows = this.element.querySelectorAll('tr.facility-row')
    const violations = []
    // Issue #13405: 再架電系（再架電・再架電（日程確定待ち））ステータス。
    const redialStatuses = ['redial', 'redial_waiting_for_schedule']
    const redialViolations = []
    rows.forEach((row) => {
      const statusSelect = row.querySelector('select[name*="[status]"]')
      if (!statusSelect) return

      // Issue #13438: ユーザーが実際に編集した行のみ検証する。変更行は値変更時に enable() が
      // 隠しの id を有効化(disabled=false)するため、それを「編集された行」の判定に使う。
      // 未編集の既存行（例: 既にDB上で見学設定済だが紹介カード依頼日/見学予定日が空のまま残っている行）は、
      // サーバー側の必須チェック・保存対象でもない（id 未送信のため facility_task_attributes['id'].nil? でスキップ）。
      // ここで全行を検証してしまうと、自分が触っていない既存の違反行のせいでフォーム全体の保存がブロックされ、
      // 入力した内容が一切保存できなくなる（#13438）。サーバーと同じ「変更行のみ検証」に揃える。
      const idField = row.querySelector('input[name*="facility_task_attributes"][name$="[id]"]')
      if (idField && idField.disabled) return

      // Issue #13405: 今回の入力で「再架電系」へ変更したのに「再架電予定日時」が未入力の行を検知する。
      // 既存が既に再架電系の行（next_call_at=NULL の移行データ等）は対象外＝サーバーガードと同じ「遷移時のみ必須」。
      if (redialStatuses.includes(statusSelect.value) &&
          !redialStatuses.includes(statusSelect.dataset.originalStatus)) {
        const nextCallInput = row.querySelector('input[name*="[next_call_at]"]')
        if (nextCallInput && !nextCallInput.disabled && nextCallInput.value.trim() === '') {
          redialViolations.push(row)
        }
      }

      if (statusSelect.value !== 'completed') return
      // Issue #13268: 見学予定日 / Issue #13285: 紹介カード依頼日。「見学設定済」化には両方必須。
      const visitInput = row.querySelector('input[name*="[scheduled_visit_at]"]')
      const cardInput = row.querySelector('input[name*="[introduction_requested_date]"]')
      // Issue #13391/#13665: 同一施設が複数ラウンドに跨る場合、見学予定日の入力は作業中の最新ラウンド行のみ編集可能で、
      // 他ラウンド行は disabled(読み取り専用)。disabled 行は当該行で見学予定日を編集・送信しないため、
      // 未入力チェックの対象外にする（編集可能行＋サーバー側ガードで必須を担保）。
      const missingVisit = visitInput && !visitInput.disabled && visitInput.value.trim() === ''
      const missingCard = cardInput && cardInput.value.trim() === ''
      if (missingVisit || missingCard) {
        violations.push({ row, missingVisit, missingCard })
      }
    })

    // 前回のハイライト/エラーをクリア（見学設定済の必須チェック用と再架電予定日時用は別クラス）
    this.element.querySelectorAll('tr.facility-row.visit-date-error, tr.facility-row.next-call-error').forEach((row) => {
      row.classList.remove('visit-date-error', 'next-call-error')
    })
    const errorBox = document.getElementById('visit-date-validation-error')

    if (violations.length === 0 && redialViolations.length === 0) {
      if (errorBox) errorBox.classList.add('d-none')
      // Issue #13282: 送信が成立するこのタイミングでスクロールコンテナ(.app-container)の位置を退避する（保存後に復元）。
      sessionStorage.setItem(this.constructor.SCROLL_KEY, String(this.scrollContainer().scrollTop))
      return
    }

    event.preventDefault()
    violations.forEach((v) => v.row.classList.add('visit-date-error'))
    // Issue #13405: 再架電系で再架電予定日時が未入力の行は専用クラスでハイライトする。
    // visit-date-error と分け、赤枠は「再架電予定日時」欄のみに限定する（見学予定日・紹介カード依頼日は赤くしない）。
    redialViolations.forEach((row) => row.classList.add('next-call-error'))

    const messages = []
    if (violations.length > 0) {
      // 不足している項目名を集約してメッセージに反映する（見学予定日 / 紹介カード依頼日）。
      const missingLabels = []
      if (violations.some((v) => v.missingVisit)) missingLabels.push('見学予定日')
      if (violations.some((v) => v.missingCard)) missingLabels.push('紹介カード依頼日')
      messages.push(`${missingLabels.join('・')}が未入力の施設が ${violations.length} 件あります。対応ステータスを「見学設定済」にするには ${missingLabels.join('・')} の入力が必要です。`)
    }
    if (redialViolations.length > 0) {
      // Issue #13405: 再架電系へ変更するのに再架電予定日時が未入力の行を案内する。
      messages.push(`再架電予定日時が未入力の施設が ${redialViolations.length} 件あります。対応ステータスを「再架電」「再架電（日程確定待ち）」にするには再架電予定日時の入力が必要です。`)
    }
    if (errorBox) {
      errorBox.textContent = `${messages.join(' ')}（赤くハイライトされた行をご確認ください）`
      errorBox.classList.remove('d-none')
    }
    const firstViolationRow = (violations[0] && violations[0].row) || redialViolations[0]
    firstViolationRow.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
